import React from "react";
import simplePageImg from "@/assets/img/simplepage.PNG";
import productPageImg from "@/assets/img/product_page.PNG";
import categoryPageImg from "@/assets/img/category_page.PNG";
import subcategoryPageImg from "@/assets/img/subcategory_page.PNG";
import postImg from "@/assets/img/post.PNG";
import authorImg from "@/assets/img/author.PNG";
import {
  Card,
  CardBody,
  CardImg,
  CardTitle,
  ListGroupItem,
  ListGroup,
  Row,
  Col,
  Container,
  FormGroup,
  Input,
  ListGroupItemHeading,
  ListGroupItemText,
  Label,
  Badge,
} from "reactstrap";
import type { RootState } from "@/services/store";
import type { PageTypeChecker } from "./PageTypesCreateView";

/**
 * Make a whole `<Card>` a real, keyboard-operable control (SCRUM-1360).
 *
 * Every clickable card on this screen carried `onClick` and a pointer cursor and
 * nothing else: no role, no tab stop, no accessible name. A keyboard-only or
 * screen-reader user could not select a page type or an entity at all
 * (WCAG 2.1.1 Keyboard, 4.1.2 Name/Role/Value).
 *
 * Why attributes rather than a `<button>`, which is what the sibling screen got in
 * SCRUM-892: there the control was an icon-only inner div and swapping the tag was
 * free. Here the CONTROL IS THE CARD, and a card wrapping a title, an image and a
 * list cannot become a `<button>` — nesting that content in a button is invalid and
 * would inherit button styling. `role="button"` + `tabIndex` + a key handler gives
 * the same semantics with the same DOM element, which is also why the resting
 * appearance cannot change: no tag changes, and the inline style is passed through
 * untouched.
 *
 * `preventDefault` on Space is not optional: on a non-button element Space scrolls
 * the page, so without it the card would activate AND jump the viewport.
 *
 * `onActivate` is what the click already did. `label` is the accessible name; it
 * CONTAINS the card's visible title, so speech input still matches what a user
 * sees (WCAG 2.5.3 Label in Name).
 */
const cardButtonProps = (onActivate: () => void, label: string) => ({
  role: "button",
  tabIndex: 0,
  "aria-label": label,
  onClick: onActivate,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  },
  style: { cursor: "pointer" },
});

type InputChange = (e: React.ChangeEvent<HTMLInputElement>) => void;

/**
 * The picker lists are `any[]` as they were in the JSDoc: the container fills
 * them from store slices typed `any[]`, and the view reads each entity's fields.
 */
export type PageTypesUpdateViewProps = {
  checker: PageTypeChecker;
  /** An entity id or a path: compared and tested for truthiness only. */
  requiresChecker: unknown;
  pathSimplePage: string;
  radioGroupValue: string;
  blogAvailability: boolean;
  postsAvailability: boolean;
  authors: any[];
  CurrentSite: RootState["sites"]["site"];
  CategoryList: any[];
  SubCategoriesList: any[];
  ProductsList: any[];

  selectType: (name: keyof PageTypeChecker) => void;
  onChangeSimplePageInputHandler: InputChange;
  onChangeRadioInputHandler: InputChange;
  onClickCategoryHandler: (category: { _id?: unknown }) => void;
  onClickSubCategoryHandler: (subCategory: { _id?: unknown }) => void;
  onClickProductHandler: (productWrapper: { product: { _id?: unknown } }) => void;
  onClickPostHandler: (post: { _id?: unknown }) => void;
  onClickAuthorHandler: (author: { _id?: unknown }) => void;
};

const PageTypesUpdateView = ({
  // state
  checker,
  requiresChecker,
  pathSimplePage,
  radioGroupValue,
  blogAvailability,
  postsAvailability,
  authors,
  CurrentSite,
  CategoryList,
  SubCategoriesList,
  ProductsList,

  // handlers
  selectType,
  onChangeSimplePageInputHandler,
  onChangeRadioInputHandler,
  onClickCategoryHandler,
  onClickSubCategoryHandler,
  onClickProductHandler,
  onClickPostHandler,
  onClickAuthorHandler,
}: PageTypesUpdateViewProps) => {
  return (
    <Container>
      <Row>
        <Col lg="12">
          <div className="card-group">
            {/* SIMPLE PAGE */}
            <Col lg="2">
              <Card
                className={
                  checker.simple_page ? "card-lift--hover shadow border-0" : " bg-secondary mb-3"
                }
                {...cardButtonProps(() => selectType("simple_page"), "Select Custom Page")}
              >
                <CardImg alt="Custom Page" src={simplePageImg} top />
                <CardBody>
                  <CardTitle>Custom Page</CardTitle>
                  {checker.simple_page ? (
                    <div><i className="ni ni-check-bold fa-3x text-green" /></div>
                  ) : (
                    <div className="div-icon-animation" data-testid="simple_page">
                      <i className="ni ni-fat-remove fa-3x text-muted" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* PRODUCT */}
            <Col lg="2">
              <Card
                className={
                  checker.product ? "card-lift--hover shadow border-0" : " bg-secondary mb-3"
                }
                {...cardButtonProps(() => selectType("product"), "Select Product")}
              >
                <CardImg alt="Product" src={productPageImg} top />
                <CardBody>
                  <CardTitle>Product</CardTitle>
                  {checker.product ? (
                    <div><i className="ni ni-check-bold fa-3x text-green" /></div>
                  ) : (
                    <div className="div-icon-animation" data-testid="product">
                      <i className="ni ni-fat-remove fa-3x text-muted" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* CATEGORY */}
            <Col lg="2">
              <Card
                className={
                  checker.category ? "card-lift--hover shadow border-0" : " bg-secondary mb-3"
                }
                {...cardButtonProps(() => selectType("category"), "Select Category")}
              >
                <CardImg alt="Category" src={categoryPageImg} top />
                <CardBody>
                  <CardTitle>Category</CardTitle>
                  {checker.category ? (
                    <div><i className="ni ni-check-bold fa-3x text-green" /></div>
                  ) : (
                    <div className="div-icon-animation" data-testid="category">
                      <i className="ni ni-fat-remove fa-3x text-muted" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* SUBCATEGORY */}
            <Col lg="2">
              <Card
                className={
                  checker.subcategory ? "card-lift--hover shadow border-0" : " bg-secondary mb-3"
                }
                {...cardButtonProps(() => selectType("subcategory"), "Select Listing")}
              >
                <CardImg alt="Subcategory" src={subcategoryPageImg} top />
                <CardBody>
                  <CardTitle>Listing</CardTitle>
                  {checker.subcategory ? (
                    <div><i className="ni ni-check-bold fa-3x text-green" /></div>
                  ) : (
                    <div className="div-icon-animation" data-testid="subcategory">
                      <i className="ni ni-fat-remove fa-3x text-muted" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* POSTS */}
            <Col lg="2">
              <Card
                className={
                  checker.posts ? "card-lift--hover shadow border-0" : " bg-secondary mb-3"
                }
                {...cardButtonProps(() => selectType("posts"), "Select Blog Posts")}
              >
                <CardImg alt="Posts" src={postImg} top />
                <CardBody>
                  <CardTitle>Blog Posts</CardTitle>
                  {checker.posts ? (
                    <div><i className="ni ni-check-bold fa-3x text-green" /></div>
                  ) : (
                    <div className="div-icon-animation" data-testid="posts">
                      <i className="ni ni-fat-remove fa-3x text-muted" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* AUTHORS */}
            <Col lg="2">
              <Card
                className={
                  checker.authors ? "card-lift--hover shadow border-0" : " bg-secondary mb-3"
                }
                {...cardButtonProps(() => selectType("authors"), "Select Blog Authors")}
              >
                <CardImg alt="Authors" src={authorImg} top />
                <CardBody>
                  <CardTitle>Blog Authors</CardTitle>
                  {checker.authors ? (
                    <div><i className="ni ni-check-bold fa-3x text-green" /></div>
                  ) : (
                    <div className="div-icon-animation" data-testid="authors">
                      <i className="ni ni-fat-remove fa-3x text-muted" />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </div>
        </Col>
      </Row>

      {/* SIMPLE PAGE FORM */}
      {checker.simple_page && (
        <FormGroup className="row">
          <Label className="form-control-label" htmlFor="example4cols1Input" md="3">
            Specify Routing path
          </Label>
          <Col md="5">
            <Input
              id="example4cols1Input"
              placeholder="example : Home, Redirect, Author ..."
              type="text"
              disabled={radioGroupValue !== "default"}
              onChange={onChangeSimplePageInputHandler}
              value={pathSimplePage}
            />

            <Row>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="default_page"
                    type="checkbox"
                    value="default"
                    checked={radioGroupValue === "default"}
                    disabled={radioGroupValue !== "default"}
                    readOnly
                  />
                  <label className="custom-control-label" htmlFor="default_page">
                    Default
                  </label>
                </div>
              </Col>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="error_page"
                    type="checkbox"
                    value="error"
                    checked={radioGroupValue === "error"}
                    disabled={radioGroupValue !== "error"}
                    onChange={onChangeRadioInputHandler}
                  />
                  <label className="custom-control-label" htmlFor="error_page">
                    Error page
                  </label>
                </div>
              </Col>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="maintenance_page"
                    type="checkbox"
                    value="maintenance"
                    checked={radioGroupValue === "maintenance"}
                    disabled={radioGroupValue !== "maintenance"}
                    onChange={onChangeRadioInputHandler}
                  />
                  <label className="custom-control-label" htmlFor="maintenance_page">
                    Maintenance page
                  </label>
                </div>
              </Col>
              <Col md="3">
                <div className="custom-control custom-checkbox">
                  <input
                    className="custom-control-input"
                    id="redirect_page"
                    type="checkbox"
                    value="redirect"
                    checked={radioGroupValue === "redirect"}
                    disabled={radioGroupValue !== "redirect"}
                    onChange={onChangeRadioInputHandler}
                  />
                  <label className="custom-control-label" htmlFor="redirect_page">
                    Redirect page
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </FormGroup>
      )}

      {/* CATEGORY LIST */}
      {checker.category && (
        <>
          <Row><h4 className="h3 text-info text-uppercase">Selected Page Category :</h4></Row>
          <Row>
            {CategoryList.map((category, index) => (
              <Col md="6" xl="3" key={category._id || index}>
                <Card
                  className={
                    requiresChecker === category._id ? "card-stats" : " bg-secondary mb-3"
                  }
                  {...cardButtonProps(() => onClickCategoryHandler(category), `Select category ${category.name}`)}
                >
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-default mb-0">
                          {category.name}
                        </CardTitle>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-red text-white rounded-circle shadow">
                          <i className="ni ni-active-40" />
                        </div>
                      </Col>
                    </Row>

                    <ListGroup flush>
                      <ListGroupItem>
                        <ListGroupItemHeading>
                          Sub Categories{" "}
                          <Badge color="primary" pill>
                            {category.subcategories.length}
                          </Badge>
                        </ListGroupItemHeading>
                        {category.subcategories.map((sc, i) => (
                          <ListGroupItemText key={sc._id || i}>{sc.name}</ListGroupItemText>
                        ))}
                      </ListGroupItem>

                      <ListGroupItem>
                        <ListGroupItemHeading>Products</ListGroupItemHeading>
                        {category.subcategories.map((sc) =>
                          // A subcategory from the site document has no `products`
                          // (SCRUM-1737); each type-options wrapper names its own
                          // subcategory instead.
                          ProductsList.filter((p) => String(p.subcategory) === String(sc._id)).map((pr) =>
                            pr.product ? (
                              <ListGroupItemText key={pr._id}>
                                {pr.product.title}
                              </ListGroupItemText>
                            ) : null
                          )
                        )}
                      </ListGroupItem>
                    </ListGroup>

                    {requiresChecker !== category._id ? (
                      <div className="div-icon-animation" data-testid={category._id}>
                        <i className="ni ni-fat-remove fa-3x text-muted" />
                      </div>
                    ) : (
                      <div>
                        <i className="ni ni-check-bold fa-3x text-green" />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* SUBCATEGORY LIST */}
      {checker.subcategory && (
        <>
          <Row><h4 className="h3 text-info text-uppercase">Selected Page Sub Category :</h4></Row>
          <Row>
            {SubCategoriesList
              .slice()
              .sort(
                (a, b) =>
                  b.products.filter((pr) => pr.product !== null).length -
                  a.products.filter((pr) => pr.product !== null).length
              )
              .map((sc) => (
                <Col md="6" xl="3" key={sc._id}>
                  <Card className="card-stats" {...cardButtonProps(() => onClickSubCategoryHandler(sc), `Select listing ${sc.name}`)}>
                    <CardBody>
                      <Row>
                        <div className="col">
                          <CardTitle tag="h5" className="text-uppercase text-default mb-0">
                            {sc.name}
                          </CardTitle>
                        </div>
                        <Col className="col-auto">
                          <div className="icon icon-shape bg-gradient-orange text-white rounded-circle shadow">
                            <i className="ni ni-chart-pie-35" />
                          </div>
                        </Col>
                      </Row>

                      <ListGroup flush>
                        <ListGroupItem>
                          <ListGroupItemHeading>Category</ListGroupItemHeading>
                          <ListGroupItemText>{sc.category.name}</ListGroupItemText>
                        </ListGroupItem>

                        <ListGroupItem>
                          <ListGroupItemHeading>
                            Products{" "}
                            <Badge color="primary" pill>
                              {sc.products.filter((p) => p.product !== null).length}
                            </Badge>
                          </ListGroupItemHeading>
                          {sc.products
                            .filter((p) => p.product !== null)
                            .map((p) => (
                              <ListGroupItemText key={p._id}>{p.product.title}</ListGroupItemText>
                            ))}
                        </ListGroupItem>
                      </ListGroup>

                      {requiresChecker !== sc._id ? (
                        <div className="div-icon-animation" data-testid={sc._id}>
                          <i className="ni ni-fat-remove fa-3x text-muted" />
                        </div>
                      ) : (
                        <div>
                          <i className="ni ni-check-bold fa-3x text-green" />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              ))}
          </Row>
        </>
      )}

      {/* PRODUCT LIST */}
      {checker.product && (
        <>
          <Row><h4 className="h3 text-info text-uppercase">Selected Page product :</h4></Row>
          <Row>
            {ProductsList.filter((p) => p.product !== null).map((pp) => (
              <Col lg="3" key={pp._id}>
                <Card {...cardButtonProps(() => onClickProductHandler(pp), `Select product ${pp.product.title}`)}>
                  <CardImg alt={pp.product.title} src={pp.product.productImg} top />
                  <ListGroup flush>
                    <ListGroupItem>
                      <ListGroupItemHeading>{pp.product.title}</ListGroupItemHeading>
                      <div className="float-right">
                        {pp.product.active ? (
                          <Badge color="success" pill>Allowed</Badge>
                        ) : (
                          <Badge color="danger" pill>Disallowed</Badge>
                        )}
                      </div>
                    </ListGroupItem>

                    <ListGroupItem>
                      <ListGroupItemHeading>Category</ListGroupItemHeading>
                      {SubCategoriesList.filter((sc) => sc._id === pp.subcategory).map((sc) => (
                        <ListGroupItemText key={sc._id}>{sc.category.name}</ListGroupItemText>
                      ))}
                    </ListGroupItem>

                    <ListGroupItem>
                      <ListGroupItemHeading>Sub Category</ListGroupItemHeading>
                      {SubCategoriesList.filter((sc) => sc._id === pp.subcategory).map((sc) => (
                        <ListGroupItemText key={sc._id}>{sc.name}</ListGroupItemText>
                      ))}
                    </ListGroupItem>

                    <ListGroupItem className="text-center">
                      {requiresChecker !== pp.product._id ? (
                        <div className="div-icon-animation" data-testid={pp.product._id}>
                          <i className="ni ni-fat-remove fa-3x text-muted" />
                        </div>
                      ) : (
                        <div>
                          <i className="ni ni-check-bold fa-3x text-green" />
                        </div>
                      )}
                    </ListGroupItem>
                  </ListGroup>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* POSTS: no picker on the update screen. It read the current site's blog posts array,
          which the current-site response never carries (SCRUM-1734); the create
          screen's picker reads the posts slice instead. */}

      {/* AUTHORS */}
      {checker.authors && postsAvailability && (
        <>
          <Row><h4 className="h3 text-info text-uppercase">Selected Page author :</h4></Row>
          <Row>
            {authors.map((author) => (
              <Col md="6" xl="3" key={author._id}>
                <Card {...cardButtonProps(() => onClickAuthorHandler(author), `Select author ${author.name}`)}>
                  <CardBody>
                    {/*
                      Was an `<a href="#">` whose only handler was preventDefault — a link
                      that went nowhere and existed for the avatar styling. Once the card
                      around it became role="button" that anchor turned into a focusable
                      control INSIDE a control: a second tab stop that announces itself as
                      a link and does nothing, which is a worse defect than the one this
                      ticket fixes. It is a span now; the classes, and so the appearance,
                      are unchanged (SCRUM-1360).
                    */}
                    <span className="avatar avatar-xl rounded-circle">
                      <CardImg alt={author.name} src={author.avatar} top />
                    </span>
                    <CardTitle tag="h5" className="text-uppercase text-primary mb-0">
                      {author.name}
                    </CardTitle>
                    <ListGroup flush>
                      <ListGroupItem>
                        <ListGroupItemHeading>Posts</ListGroupItemHeading>
                        {author.posts.map((pt, i) => (
                          <div className="progress-label" key={`${author._id}_${i}`}>
                            <span>{pt}</span>
                            <br />
                          </div>
                        ))}
                      </ListGroupItem>
                    </ListGroup>
                  </CardBody>

                  {requiresChecker !== author._id ? (
                    <div className="div-icon-animation" data-testid={author._id}>
                      <i className="ni ni-fat-remove fa-3x text-muted" />
                    </div>
                  ) : (
                    <div><i className="ni ni-check-bold fa-3x text-green" /></div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Container>
  );
};

export default PageTypesUpdateView;
